import React, { useState, useEffect } from 'react';
import Button from 'antd/lib/button';
import Overlay from '../Overlay';
import { API, graphqlOperation } from 'aws-amplify';
import { useSelector, useDispatch } from 'react-redux';
import message from 'antd/lib/message';

const getTermsQuery = `
  query GetTermsOfUse($pk: ID!) {
    getTermsOfUse(pk: $pk, sk: "TERMS#LATEST") {
      text
      version
      enabled
      requireReacceptance
    }
  }
`;

const updateOccupantQuery = `
  mutation UpdateOccupant($pk: ID!, $sk: String!, $expectedVersion: Int!, $input: updateOccupantInput!) {
    updateOccupant(pk: $pk, sk: $sk, expectedVersion: $expectedVersion, input: $input) {
      version
      extraFields {
        key
        value
      }
    }
  }
`;

const styles = {
    container: {
        padding: '1em',
        width: 505,
        maxHeight: '70vh',
        overflow: 'auto'
    },
    content: {
        whiteSpace: 'pre-wrap',
        marginBottom: '15px',
        minHeight: 380,
        backgroundColor: '#fff',
        padding: '15px',
        border: '1px solid #d9d9d9',
        borderRadius: '4px'
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '15px'
    },
    headerText: {
        textAlign: 'center',
        width: '100%',
        fontSize: '24px',
        fontWeight: 600,
        marginBottom: '15px',
        fontFamily: 'Avenir, sans-serif'
    }
};

const TermsOfUseAcceptance = ({ onVisibilityChange }) => {
    const dispatch = useDispatch();

    const [termsText, setTermsText] = useState('');
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(true);

    const user = useSelector(state => state.app.user);
    const buildingId = useSelector(state => state.app.building?.pk);
    const buildingSettings = useSelector(state => state.app.building?.settings);
    const isSuperAdmin = useSelector(state => state.app.user?.roles?.includes('SUPER_ADMIN'));

    // Check immediately if Terms of Use might be needed (before async operations)
    useEffect(() => {
        const termsEnabled = buildingSettings?.systemConfig?.termsOfUse;
        const hasRequiredUserData = user?.firstName && user?.lastName && user?.d1;

        if (termsEnabled && buildingId && hasRequiredUserData) {
            // Preemptively show to prevent other modals from flashing
            setVisible(true);
            if (onVisibilityChange) {
                onVisibilityChange(true);
            }
        }
    }, [buildingId, user, buildingSettings, onVisibilityChange]);

    useEffect(() => {
        const checkTermsAcceptance = async () => {
            if (!buildingId || !user) {
                setLoading(false);
                return;
            }

            // Check if user configuration is complete (user has basic required fields)
            // Instead of waiting for haveLoggedIn, check if user has completed the initial setup
            const hasRequiredUserData = user.firstName && user.lastName && user.d1;
            if (!hasRequiredUserData) {
                setLoading(false);
                if (onVisibilityChange) {
                    onVisibilityChange(false);
                }
                return;
            }

            // First check if terms of use is enabled in building settings
            const termsEnabled = buildingSettings?.systemConfig?.termsOfUse;
            if (!termsEnabled) {
                setLoading(false);
                if (onVisibilityChange) {
                    onVisibilityChange(false);
                }
                return;
            }

            try {
                setLoading(true);
                const response = await API.graphql(graphqlOperation(getTermsQuery, {
                    pk: buildingId
                }));

                const terms = response?.data?.getTermsOfUse;
                if (!terms || !terms.enabled) {
                    setLoading(false);
                    setVisible(false);
                    if (onVisibilityChange) {
                        onVisibilityChange(false);
                    }
                    return;
                }

                // Validate terms version
                if (terms.version < 1) {
                    console.error('[Terms Check] Invalid terms version:', terms.version);
                    setLoading(false);
                    setVisible(false);
                    if (onVisibilityChange) {
                        onVisibilityChange(false);
                    }
                    return;
                }

                setTermsText(terms.text || '');

                // Get the user's accepted version from extraFields for this specific building
                const acceptedVersionField = user.extraFields?.find(field => field.key === `acceptedTermsVersion_${buildingId}`);
                const acceptedVersion = acceptedVersionField ? parseInt(acceptedVersionField.value, 10) : 0;

                // Validate accepted version
                if (acceptedVersion < 0) {
                    console.error('[Terms Check] Invalid accepted version:', acceptedVersion);
                    setLoading(false);
                    setVisible(false);
                    if (onVisibilityChange) {
                        onVisibilityChange(false);
                    }
                    return;
                }

                // Show modal if:
                // 1. User hasn't accepted any version for this building
                // 2. Terms version is higher than accepted version AND requireReacceptance is true
                const shouldShowTerms = !acceptedVersion || (terms.version > acceptedVersion && terms.requireReacceptance);

                setVisible(shouldShowTerms);
                if (onVisibilityChange) {
                    onVisibilityChange(shouldShowTerms);
                }
            } catch (error) {
                console.error('[Terms Check] Error checking terms:', error);
                message.error('Error checking terms of use status');
                setVisible(false);
                if (onVisibilityChange) {
                    onVisibilityChange(false);
                }
            } finally {
                setLoading(false);
            }
        };

        // Only check terms when we have both user and building context
        if (buildingId && user) {
            checkTermsAcceptance();
        }
    }, [buildingId, user, buildingSettings]);

    const handleAccept = async () => {
        if (!buildingId || !user) {
            message.error('Missing required context for accepting terms');
            return;
        }

        setLoading(true);
        try {
            // Get the latest user state in case it was updated by other modals
            const { store } = require('../../redux');
            const currentUser = store.getState().app.user;
            // Get current terms version
            const termsResponse = await API.graphql(graphqlOperation(getTermsQuery, {
                pk: buildingId
            }));

            const terms = termsResponse?.data?.getTermsOfUse;
            if (!terms) {
                throw new Error('Could not get current terms version');
            }

            // Add validation for terms version
            if (terms.version < 1) {
                console.error('[Terms Error] Invalid terms version:', terms.version);
                message.error('Invalid terms version. Please contact support.');
                return;
            }

            // Get current accepted version
            const currentAcceptedVersionField = currentUser.extraFields?.find(field => field.key === `acceptedTermsVersion_${buildingId}`);
            const currentAcceptedVersion = currentAcceptedVersionField ? parseInt(currentAcceptedVersionField.value, 10) : 0;

            // Validate that new version is greater than current accepted version
            if (terms.version <= currentAcceptedVersion) {
                console.error('[Terms Error] New version not greater than accepted version:', {
                    currentAcceptedVersion,
                    newVersion: terms.version
                });
                message.error('Invalid terms version. Please contact support.');
                return;
            }

            // Update extraFields with accepted version for this specific building
            const extraFields = [...(currentUser.extraFields || [])];
            const versionIndex = extraFields.findIndex(field => field.key === `acceptedTermsVersion_${buildingId}`);

            if (versionIndex >= 0) {
                extraFields[versionIndex].value = terms.version.toString();
            } else {
                extraFields.push({
                    key: `acceptedTermsVersion_${buildingId}`,
                    value: terms.version.toString()
                });
            }

            // Try the update with retry logic for version conflicts
            let response;
            let retryCount = 0;
            const maxRetries = 3;

            while (retryCount < maxRetries) {
                try {
                    response = await API.graphql(graphqlOperation(updateOccupantQuery, {
                        pk: currentUser.pk,
                        sk: currentUser.sk,
                        expectedVersion: currentUser.version,
                        input: {
                            extraFields
                        }
                    }));
                    break; // Success, exit retry loop
                } catch (error) {
                    if (error.errors?.[0]?.errorType === 'DynamoDB:ConditionalCheckFailedException' && retryCount < maxRetries - 1) {
                        // Version conflict, wait and retry - get fresh user state
                        retryCount++;

                        // Force a small delay to allow other operations to complete
                        await new Promise(resolve => setTimeout(resolve, 1000));

                        // Get fresh user object from Redux state
                        const { store: freshStore } = require('../../redux');
                        const freshUser = freshStore.getState().app.user;
                        if (freshUser && freshUser.version > currentUser.version) {
                            // Update the currentUser reference for the next iteration
                            Object.assign(currentUser, freshUser);
                        }
                        continue;
                    }
                    throw error; // Re-throw if not a version conflict or max retries reached
                }
            }

            if (response?.data?.updateOccupant) {
                dispatch({
                    type: 'UPDATE_USER',
                    payload: {
                        ...user,
                        extraFields: response.data.updateOccupant.extraFields || extraFields,
                        version: response.data.updateOccupant.version
                    }
                });
                // Dispatch event to notify other components that occupant data was updated
                window.dispatchEvent(new CustomEvent('occupant-data-updated'))
                setVisible(false);
                if (onVisibilityChange) {
                    onVisibilityChange(false);
                }
                message.success('Terms of Use accepted');
            }
        } catch (error) {
            console.error('[Terms Error] Error accepting terms:', error);
            message.error('Failed to accept Terms of Use. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!visible || loading || !buildingId || !user) {
        return null;
    }

    return (
        <Overlay
            visible={visible}
            closebutton={false}
            headerText=""
            minimal={true}
            style={{ borderTop: 'none' }}
            zIndex={2010}
        >
            <div style={styles.container}>
                <div style={styles.headerText}>Terms of Use</div>
                <div style={styles.content}>
                    {loading ? 'Loading...' : termsText}
                </div>
                <div style={styles.buttonContainer}>
                    <Button
                        type="primary"
                        onClick={handleAccept}
                        size="large"
                        disabled={loading}
                    >
                        Accept
                    </Button>
                </div>
            </div>
        </Overlay>
    );
};

export default TermsOfUseAcceptance; 